import { useCallback, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer';
import { getLogger } from '@confluence/logger';
import { useLoomEntryPointVariant } from '@confluence/loom-utils/entry-points/useLoomEntryPointVariant';
import { confluenceLocalStorageInstance as localStorage } from '@confluence/storage-manager/entry-points/storageInstance';

import { USER_HAS_CLICKED_ON_MEETING_NOTES_TEMPLATE_KEY } from './localStorageKeys';

const flagId = 'loomAiMeetingNotesPostProvisionFlag';
const logger = getLogger('useLoomAiMeetingNotesPostProvisionFlag');

const i18n = {
	title: {
		id: 'loom-utils.loom-ai-meeting-notes-post-provision-flag.title',
		defaultMessage: 'You’ve added Loom',
		description:
			'Title of the flag that shows when Loom is provisioned via the Confluence AI meeting notes touchpoint',
	},
	description: {
		id: 'loom-utils.loom-ai-meeting-notes-post-provision-flag.description',
		defaultMessage:
			'You can now record and embed videos by typing ‘/’ followed by ‘loom’ whilst editing.',
		description:
			'Description of the flag that shows when Loom is provisioned via the Confluence AI meeting notes touchpoint',
	},
	ctaText: {
		id: 'loom-utils.loom-ai-meeting-notes-post-provision-flag.ctaText',
		defaultMessage: 'Got it',
		description: 'Text for the call to action button on the Loom post-provision flag',
	},
};

export interface UseLoomAiMeetingNotesPostProvisionFlagProps {
	flags: FlagsStateContainer;
	/**
	 * Whether to show the flag when variant changes to CO_USE
	 */
	enabled?: boolean;
}

/**
 * Hook that shows a flag when Loom is successfully provisioned via the Confluence AI meeting notes touchpoint.
 * This indicates that the user is now eligible to join their team on Loom.
 */
export const useLoomAiMeetingNotesPostProvisionFlag = ({
	flags,
	enabled = true,
}: UseLoomAiMeetingNotesPostProvisionFlagProps) => {
	const { formatMessage } = useIntl();
	const { entryPointVariant, loading } = useLoomEntryPointVariant();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const previousVariantRef = useRef<string | undefined>();

	const handleCtaClick = useCallback(async () => {
		await flags.hideFlag(flagId);
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'gotIt',
				source: 'loomAiMeetingNotesPostProvisioningFlag',
			},
		}).fire();
	}, [flags, createAnalyticsEvent]);

	useEffect(() => {
		// Only show flag if the user has seen the touchpoint
		const hasClickedOnMeetingNotesTemplate = localStorage.getItemAsBoolean(
			USER_HAS_CLICKED_ON_MEETING_NOTES_TEMPLATE_KEY,
		);

		// Only show flag if enabled and variant changed to CO_USE
		if (
			!enabled ||
			entryPointVariant !== 'CO_USE' ||
			!hasClickedOnMeetingNotesTemplate ||
			loading
		) {
			if (!loading) {
				previousVariantRef.current = entryPointVariant;
			}
			logger.info`Ineligible to show Loom post-provision flag, conditions not met (enabled: ${enabled}, entryPointVariant: ${entryPointVariant}, hasClickedOnMeetingNotesTemplate: ${hasClickedOnMeetingNotesTemplate}, loading: ${loading})`;
			return;
		}

		// Only show flag if this is a change to CO_USE, not the initial state
		if (previousVariantRef.current !== undefined && previousVariantRef.current !== 'CO_USE') {
			void flags.showSuccessCircleFlag({
				id: flagId,
				title: formatMessage(i18n.title),
				description: formatMessage(i18n.description),
				actions: [
					{
						content: formatMessage(i18n.ctaText),
						onClick: handleCtaClick,
					},
				],
				isAutoDismiss: false,
				onClose: () => {
					createAnalyticsEvent({
						type: 'sendUIEvent',
						data: {
							action: 'clicked',
							actionSubject: 'button',
							actionSubjectId: 'close',
							source: 'loomAiMeetingNotesPostProvisioningFlag',
						},
					}).fire();
				},
			});

			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					action: 'rendered',
					actionSubject: 'loomAiMeetingNotesPostProvisioningFlag',
				},
			}).fire();

			logger.info`Showing Loom post-provision flag as entry point variant changed to CO_USE from ${previousVariantRef.current}`;
		}

		previousVariantRef.current = entryPointVariant;
	}, [
		entryPointVariant,
		enabled,
		flags,
		formatMessage,
		loading,
		createAnalyticsEvent,
		handleCtaClick,
	]);

	return {
		/**
		 * Current touchpoint variant
		 */
		entryPointVariant,
		/**
		 * Whether the flag would be shown for CO_USE state
		 */
		isEligibleForCoUse: entryPointVariant === 'CO_USE',
	};
};
